import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Brush, Layers, Truck, Frame, Store } from "lucide-react";
import { API, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import FramedPreview from "@/components/FramedPreview";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow, HeroLines } from "@/components/Reveal";

const ASSURANCES = [
  { icon: Brush, title: "Signed originals, never prints", text: "Every card is individually hand painted and signed. No reproductions — when a card sells, it's gone." },
  { icon: Layers, title: "140 lb Arches paper", text: "Heavyweight cold-pressed Arches cotton watercolor paper — the watercolorist's standard for over five centuries." },
  { icon: Truck, title: "Mailed free, protected", text: "Every card ships free — sealed in its protective sleeve with its own envelope, flat-packed in a rigid mailer." },
];

const QUARTERS = [
  { id: "q1", kicker: "First Quarter", label: "January — March", months: ["January", "February", "March"] },
  { id: "q2", kicker: "Second Quarter", label: "April — June", months: ["April", "May", "June"] },
  { id: "q3", kicker: "Third Quarter", label: "July — September", months: ["July", "August", "September"] },
  { id: "q4", kicker: "Fourth Quarter", label: "October — December", months: ["October", "November", "December"] },
];

const quarterOf = (card) => {
  if (QUARTERS.some((q) => q.id === card.quarter)) return card.quarter;
  const m = (card.description || "").match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+20\d\d/);
  if (!m) return null;
  const q = QUARTERS.find((qt) => qt.months.includes(m[1]));
  return q ? q.id : null;
};

export default function Notecards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    API.get("/notecards").then((r) => setCards(r.data.filter((c) => c.status !== "soon" && c.status !== "retired"))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="notecards-page">
      <Seo title="Hand-Painted Notecards" description="One-of-one original 3 × 5 inch watercolor notecards by Richard Kuensting — signed, on 140 lb Arches paper, with envelope and protective sleeve. $7 each, free mailing; frame any card to suit your taste and decor." />
      <header className="max-w-7xl mx-auto px-5 sm:px-10 pt-16 sm:pt-24 pb-12">
        <Eyebrow>The Card Table</Eyebrow>
        <HeroLines lines={["One-of-one cards,", "painted by hand."]}
          className="font-serif font-light text-ink text-4xl sm:text-6xl mt-4 leading-[1.05] tracking-tight" />
        <p className="text-stone2 mt-6 max-w-xl text-sm sm:text-base leading-relaxed">
          Small original watercolors painted in the studio — composed from sketches, memory, and reference photographs, and painted for feeling rather than any one exact place. Each card is an original 3 × 5 inch watercolor — signed, sleeved, and the only one of its kind.
        </p>
      </header>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-16 sm:py-24">
        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone2" data-testid="notecards-loading">Laying out the card table…</p>
        ) : (
          <div className="space-y-16 sm:space-y-20" data-testid="notecards-grid">
            {QUARTERS.map((q) => {
              const qCards = cards.filter((c) => quarterOf(c) === q.id);
              return (
                <div key={q.id} data-testid={`quarter-${q.id}`}>
                  <Reveal className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-ink/10 pb-4 mb-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">{q.kicker}</p>
                    <h2 className="font-serif text-2xl sm:text-3xl text-ink">{q.label}</h2>
                    {qCards.length > 0 && (
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone2 ml-auto">{qCards.length} on the table</p>
                    )}
                  </Reveal>
                  {qCards.length === 0 ? (
                    <Reveal>
                      <p className="font-serif text-xl text-stone2 italic" data-testid={`quarter-empty-${q.id}`}>Painting now — this table arrives with the season.</p>
                    </Reveal>
                  ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
                      {qCards.map((card, i) => (
                        <Reveal key={card.id} delay={(i % 4) * 0.07}>
                          <article className="group" data-testid={`notecard-${card.id}`}>
                            <div className="relative overflow-hidden bg-[#E8E0D0] border border-ink/10 deckle aspect-[4/5]">
                              <img src={card.image} alt={`${card.title} — original hand-painted watercolor notecard by Richard Kuensting`} loading="lazy"
                                className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05] ${card.status === "sold" ? "opacity-70 grayscale-[35%]" : ""}`} />
                              <span className={`absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border backdrop-blur ${card.status === "sold" ? "border-stone2/40 bg-paper/85 text-stone2" : card.status === "soon" ? "border-ochre/40 bg-paper/85 text-ochre" : "border-terracotta/30 bg-paper/85 text-terracotta"}`}>
                                {card.status === "sold" ? "Sold — collected" : card.status === "soon" ? "In the studio — soon" : "One of one"}
                              </span>
                            </div>
                            <div className="mt-4">
                              <h2 className="font-serif text-lg sm:text-xl text-ink leading-snug">{card.title}</h2>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage mt-1">
                                {card.status === "sold" ? "In a private collection" : card.status === "soon" ? "Coming to the table soon" : "Original watercolor · signed"}
                              </p>
                              <div className="flex items-center justify-between mt-3 gap-3">
                                <p className="font-serif text-xl text-terracotta">{card.status === "sold" ? "—" : fmtPrice(card.price_cents)}</p>
                                {card.status === "sold" ? (
                                  <span data-testid={`notecard-sold-${card.id}`} className="inline-flex items-center gap-1.5 border border-stone2/30 rounded-full px-4 py-2 text-[10px] font-mono uppercase tracking-[0.15em] text-stone2">
                                    Found its home
                                  </span>
                                ) : card.status === "soon" ? (
                                  <span data-testid={`notecard-soon-${card.id}`} className="inline-flex items-center gap-1.5 border border-ochre/30 rounded-full px-4 py-2 text-[10px] font-mono uppercase tracking-[0.15em] text-ochre">
                                    In the studio
                                  </span>
                                ) : (
                                  <button
                                    data-testid={`notecard-add-to-cart-${card.id}`}
                                    onClick={() => add(card)}
                                    className="inline-flex items-center gap-1.5 border border-ink/20 rounded-full px-4 py-2 text-[10px] font-mono uppercase tracking-[0.15em] hover:bg-ink hover:text-linen transition-colors duration-300"
                                  >
                                    <Plus size={12} /> Add
                                  </button>
                                )}
                              </div>
                              {card.status === "available" && (
                                <button
                                  data-testid={`notecard-preview-framed-${card.id}`}
                                  onClick={() => setPreview(card)}
                                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-stone2 hover:text-terracotta transition-colors py-1.5"
                                >
                                  <Frame size={12} /> See it framed
                                </button>
                              )}
                            </div>
                          </article>
                        </Reveal>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <Reveal className="mt-14 text-center space-y-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone2" data-testid="notecards-footnote">
            Every card $7.00 · signed · envelope &amp; protective sleeve · free mailing
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone2">
            Any card can be framed to match your taste and decor — try &ldquo;See it framed&rdquo; below each card
          </p>
        </Reveal>
      </section>

      {/* FROM SKETCH TO SLEEVE — process, in his own photographs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 pb-16 sm:pb-24" data-testid="process-section">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <img src="/images/process-flatlay.jpg" alt="From field sketch to finished card — the working table with notes, card, and envelopes"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 01 — The working table, in Richard's own photograph</p>
          </Reveal>
          <Reveal delay={0.12}>
            <Eyebrow>From Sketch to Sleeve</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-4xl text-ink mt-3 leading-tight">How every card is made</h2>
            <ol className="mt-7 space-y-6">
              {[
                ["The field sketch", "Quick pencil divisions — foreground, middle ground, horizon — with the elemental shapes noted in the margin."],
                ["The painting", "Made in the studio from sketch, memory, and photographs. Watercolor on 140 lb Arches, signed and dated by hand."],
                ["The sending", "Slipped into a protective sleeve with its own envelope, flat-packed, and mailed free to your door."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-5">
                  <span className="font-serif italic text-3xl text-terracotta/80 leading-none">{i + 1}.</span>
                  <div>
                    <p className="font-serif text-xl text-ink">{t}</p>
                    <p className="text-sm text-stone2 leading-relaxed mt-1">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* FRAMING EXPLAINER */}
      <section className="bg-cream border-y border-ink/10" data-testid="framing-explainer">
        <div className="max-w-7xl mx-auto px-5 sm:px-10 py-16 sm:py-20">
          <Reveal>
            <Eyebrow className="text-terracotta">Imagine the Possibilities</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-4xl text-ink mt-3 leading-tight">Small artwork, big impact</h2>
            <p className="text-sm sm:text-base text-stone2 leading-relaxed mt-4 max-w-3xl">
              Any card can be framed to match your tastes and decor — a quiet wall, a bookshelf, a desk corner. A generous mat and a simple frame give a small original real presence, as these framed cards from Richard's own home show.
            </p>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mt-12">
          <Reveal>
            <div>
              <img src="/images/framed-closeup.jpg" alt="A hand-painted card from the studio, framed with a generous mat and rustic wood frame" loading="lazy"
                data-testid="framed-example-closeup" className="w-full aspect-[4/3] object-cover border border-ink/10 deckle" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">A card from the table, framed</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex gap-5 items-start">
              <Store size={26} className="text-terracotta shrink-0 mt-1" />
              <div>
                <Eyebrow className="text-terracotta">Shops &amp; Galleries</Eyebrow>
                <h2 className="font-serif text-2xl sm:text-3xl text-ink mt-3 leading-tight">Wholesale inquiries welcome</h2>
                <p className="text-sm sm:text-base text-stone2 leading-relaxed mt-4 max-w-lg">
                  Stock the card table in your shop or gallery. Wholesale buyers and retail partners — write to the studio for terms and availability.
                </p>
                <Link to="/contact" data-testid="wholesale-inquiry-link"
                  className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-ink/20 rounded-full px-6 py-3 hover:bg-ink hover:text-linen transition-colors duration-300">
                  Inquire about wholesale
                </Link>
              </div>
            </div>
          </Reveal>
          </div>
          <Reveal className="mt-12 md:w-3/5 md:mx-auto">
            <img src="/images/studio-framed-hill.jpg" alt="A framed hand-painted landscape card hanging on the wall at home" loading="lazy"
              data-testid="framed-example-wall" className="w-full aspect-[4/3] object-cover border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3 text-center">On the wall at home</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-night text-linen spotlight">
        <div className="max-w-7xl mx-auto px-5 sm:px-10 py-20 sm:py-28 grid md:grid-cols-3 gap-10">
          {ASSURANCES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.1}>
              <a.icon size={22} className="text-ochre" />
              <h3 className="font-serif text-xl mt-4">{a.title}</h3>
              <p className="text-sm text-linen/60 leading-relaxed mt-2">{a.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <FramedPreview card={preview} open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }} />
    </div>
  );
}
